/**
 * Video Action - WHAT Section
 *
 * Edit mode UI for assigning a video.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-video.hbs
 *
 * Fields:
 * - reminderOnId: Selected video ID
 * - reminderOnType: 'Video' (set automatically)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchVideos } from 'src/models/video/video';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Video WHAT Section - Edit mode
 */
export const VideoWhatSection: FC<ActionWhatSectionProps> = ({
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available videos
  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['videos', 'options'],
    queryFn: () =>
      fetchVideos({
        direct_upload: true,
        fetch_all: true,
        pluck_columns: 'id,name',
        sort: 'name asc',
      }),
    staleTime: 60000,
  });

  const videoOptions = videos.map((v) => ({
    value: v.id.toString(),
    label: v.name,
  }));

  const handleVideoChange = (videoId: string) => {
    setFieldValue(`automations.${index}.reminderOnId`, videoId);
    setFieldValue(`automations.${index}.reminderOnType`, 'Video');
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.video.assign')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={videoOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.video.selectVideo')}
            onChange={(e) => handleVideoChange(e.target.value as string)}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Video What Summary Component
 */
export const VideoWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: videos = [] } = useQuery({
    queryKey: ['videos', 'options'],
    queryFn: () =>
      fetchVideos({
        direct_upload: true,
        fetch_all: true,
        pluck_columns: 'id,name',
        sort: 'name asc',
      }),
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.video.noVideoSelected')}</i>;
  }

  const video = videos.find(
    (v) => v.id.toString() === automation.reminderOnId?.toString()
  );
  const videoName = video?.name || `Video #${automation.reminderOnId}`;

  return (
    <>
      {t('automations.video.assign')} <b>{videoName}</b>
    </>
  );
};
